import React, {useEffect} from "react"
import styled from "styled-components"

import Flex from "../../Flex"
import {Body} from "../../Typography"
import Icon, {IconTypes} from "../../Icon"

export const StyledLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: initial;
`

interface RowProps {
  isFocused: boolean
  isDisabled?: boolean
  showBorder?: boolean
  isNestedSelect?: boolean
}

interface StyledRowProps {
  heightFromTop: number
  leftAligned?: boolean
  visible?: boolean
}

export const FlexRow = styled(Flex)<RowProps>`
  background-color: ${({theme, isFocused}) => (isFocused ? theme.colors.utility.highlight : theme.colors.greys.white)};
  padding: ${({theme}) => theme.spacing.oneAndHalf};
  ${({isNestedSelect, theme}) => isNestedSelect && `border: 1px solid ${theme.colors.greys.grey20};`};

  &:hover {
    background-color: ${({theme, isDisabled}) => !isDisabled && theme.colors.utility.highlight};
  }
  ${({showBorder, theme}) =>
    showBorder &&
    `
    border-radius: 2px;
    border: 1px solid ${theme.colors.primary1};
    box-shadow: ${theme.shadows.focus} ;
  `};
`

const DEFAULT_SELECT_HEIGHT = 46
const StyledFlexRow = styled(FlexRow)<StyledRowProps>`
  position: absolute;
  width: 100%;
  top: ${({heightFromTop}) => (heightFromTop === 0 ? 0 : heightFromTop * DEFAULT_SELECT_HEIGHT)}px;
  ${({leftAligned}) => (leftAligned ? "left: calc(-100% - 2px)" : "right: calc(-100% - 2px)")};
  ${({visible}) => !visible && "display: none;"};
`

const SelectOption: React.FC = ({
  innerRef,
  innerProps,
  data,
  selectProps,
  label,
  isFocused,
  isDisabled,
  iconAfter,
  useBody
}: {
  innerRef: any
  innerProps: any
  data: any
  selectProps: any
  label: string
  isFocused: boolean
  isDisabled: boolean
  iconAfter?: IconTypes
  useBody?: boolean
}) => {
  const {
    components: {OptionIcon, OptionLabel},
    labelKey,
    focusedOptionIndex,
    updateFocusedOption
  } = selectProps

  useEffect(() => {
    if (updateFocusedOption && isFocused && !data?.parentIndex && data?.parentIndex !== 0) {
      updateFocusedOption(data.optionIndex, data?.isOptionParent)
    }
  }, [isFocused])

  const labelText = OptionLabel ? <OptionLabel {...data} /> : label || data[labelKey]
  const isNestedSelect = selectProps?.options?.find(({isOptionParent}) => isOptionParent)

  if (data?.nestedOptionIndex) {
    const visible = focusedOptionIndex === data?.parentIndex
    return (
      <StyledFlexRow
        ref={innerRef}
        {...innerProps}
        isFocused={isFocused}
        isDisabled={isDisabled}
        alignItems="center"
        visible={visible}
        heightFromTop={data.nestedOptionIndex - 1}
        leftAligned={selectProps?.leftAligned}
        isNestedSelect={isNestedSelect}>
        {OptionIcon && <OptionIcon {...data} />}
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          {useBody ? <Body small>{labelText}</Body> : <StyledLabel>{labelText}</StyledLabel>}
          {iconAfter && <Icon icon={iconAfter} size={11} />}
        </Flex>
      </StyledFlexRow>
    )
  }

  return (
    <FlexRow
      ref={innerRef}
      {...innerProps}
      isFocused={isFocused}
      isDisabled={isDisabled}
      alignItems="center"
      showBorder={data?.isOptionParent && data?.optionIndex === focusedOptionIndex}
      isNestedSelect={isNestedSelect}>
      {OptionIcon && <OptionIcon {...data} />}
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        {useBody ? <Body small>{labelText}</Body> : <StyledLabel>{labelText}</StyledLabel>}
        {iconAfter && <Icon icon={iconAfter} size={11} />}
        {data?.isOptionParent && <Icon icon="chevronRight" size={11} />}
      </Flex>
    </FlexRow>
  )
}

export default SelectOption
